import React, { useState } from "react";
import { makeStyles, MenuItem, Typography } from "@material-ui/core";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import HighlightOffRoundedIcon from "@material-ui/icons/HighlightOffRounded";
import DiseaseDetailDialog from "../../../../../../common/diseaseDetailDialog/DiseaseDetailDialog";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import {
  changeSelectedFamilyBackgroundsAction,
  changeSelectedPatientBackgroundsAction,
} from "../../../../../../../reducers/searchOmimState";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import Select from "@material-ui/core/Select";

const useStyles = makeStyles((theme) => ({
  diseaseBlock: {
    display: "flex",
    alignItems: "center",
  },
  disease: {
    width: "100%",
    borderRadius: 30,
    padding: theme.spacing(1),
    backgroundColor: "#F3F2FC",
    border: "1px solid #FFF",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    [theme.breakpoints.down("xs")]: {
      gap: "0.5rem",
    },
  },
  diseaseGridItem: {
    padding: theme.spacing(0, 1),
  },
  selectedDiseaseName: {
    textOverflow: "ellipsis",
    maxWidth: 200,
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  select: {
    width: "100%",
  },
  textField: {
    width: "100%",
    border: `2px solid grey`,
    borderRadius: 20,
    maxHeight: 39,
    "& label.Mui-focused": {
      borderRadius: 20,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderRadius: 20,
      },
    },
  },
  iconsContainer: {
    display: "flex",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  selectedContent: {
    height: "100%",
    display: "grid",
    gap: "0.5rem",
  },
  firstCap: {
    textTransform: "lowercase !important",
    "&:first-letter": {
      textTransform: "capitalize !important",
    },
  },
}));

const SelectedBackgroundsComponent = ({ hideInheritance }) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();

  const { selectedFamilyBackgrounds, selectedPatientBackgrounds } = useSelector((state) => state.searchOmim);

  const [diseaseId, setDiseaseId] = useState(null);
  const [title, setTitle] = useState(null);
  const [symptoms_score, setSymptoms_score] = useState(0);

  const handleOpenDiseaseModal = (disease) => {
    setDiseaseId(disease.id);
    setTitle(disease?.title || "");
    setSymptoms_score(disease?.symptoms_score);
  };

  const handleCloseDiseaseModal = () => {
    setDiseaseId(null);
    setTitle(null);
    setSymptoms_score(0);
  };

  const handleSetRelative = (event, disease, index) => {
    const formattedDisease = { ...disease };

    formattedDisease.relative = event.target.value;
    const newDiseases = [...selectedFamilyBackgrounds];
    newDiseases[index] = formattedDisease;

    dispatch(changeSelectedFamilyBackgroundsAction(newDiseases));
  };

  const handleSetInheritance = (event, disease, index) => {
    const formattedDisease = { ...disease };

    formattedDisease.inheritance = event.target.value;

    const newDiseases = [...selectedFamilyBackgrounds];
    newDiseases[index] = formattedDisease;

    dispatch(changeSelectedFamilyBackgroundsAction(newDiseases));
  };

  const handleRemoveDisease = (disease) => {
    if (hideInheritance) {
      const newDiseases = selectedPatientBackgrounds.filter((d) => d.id !== disease.id);
      dispatch(changeSelectedPatientBackgroundsAction(newDiseases));
    } else {
      const newDiseases = selectedFamilyBackgrounds.filter((d) => d.id !== disease.id);
      dispatch(changeSelectedFamilyBackgroundsAction(newDiseases));
    }
  };

  return (
    <>
      <Typography variant={"h2"}>{intl.formatMessage({ id: "selectedBackgrounds" })}</Typography>
      <div className={classes.selectedContent}>
        {hideInheritance
          ? selectedPatientBackgrounds.map((disease) => (
              <div className={classes.diseaseBlock} key={disease.id}>
                <Grid container key={disease.id} className={classes.disease}>
                  <Grid item xs={hideInheritance ? 12 : 4} sm={4} className={classes.diseaseGridItem}>
                    <Typography className={classes.selectedDiseaseName}>{disease.title}</Typography>
                  </Grid>
                </Grid>
                <div className={classes.iconsContainer}>
                  <IconButton onClick={() => handleOpenDiseaseModal(disease)}>
                    <VisibilityOutlinedIcon color={"secondary"} />
                  </IconButton>
                  <IconButton onClick={() => handleRemoveDisease(disease)}>
                    <HighlightOffRoundedIcon />
                  </IconButton>
                </div>
              </div>
            ))
          : selectedFamilyBackgrounds.map((disease, index) => (
              <div className={classes.diseaseBlock} key={disease.id}>
                <Grid container key={disease.id} className={classes.disease}>
                  <Grid item xs={hideInheritance ? 12 : 4} sm={4} className={classes.diseaseGridItem}>
                    <Typography className={classes.selectedDiseaseName}>{disease.title}</Typography>
                  </Grid>
                  {!hideInheritance && (
                    <>
                      <Grid item xs={12} sm={4} className={classes.diseaseGridItem}>
                        <TextField
                          className={classes.textField}
                          placeholder={intl.formatMessage({ id: "relative" }) + "*"}
                          value={disease.relative || ""}
                          variant={"outlined"}
                          onChange={(event) => handleSetRelative(event, disease, index)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} className={classes.diseaseGridItem}>
                        <Select
                          displayEmpty
                          placeholder={intl.formatMessage({ id: "inheritance" }) + "*"}
                          className={classes.textField}
                          onChange={(event) => handleSetInheritance(event, disease, index)}
                          value={disease.inheritance || ""}
                          renderValue={() =>
                            !!disease?.inheritance ? (
                              <MenuItem>
                                <Typography className={classes.firstCap}>{disease.inheritance}</Typography>
                              </MenuItem>
                            ) : (
                              <MenuItem value={""} disabled>
                                {intl.formatMessage({ id: "inheritance" }) + "*"}
                              </MenuItem>
                            )
                          }
                        >
                          <MenuItem value={""} disabled>
                            {intl.formatMessage({ id: "inheritance" }) + "*"}
                          </MenuItem>
                          <MenuItem value={"maternal"}>{intl.formatMessage({ id: "inheritance_maternal" })}</MenuItem>
                          <MenuItem value={"paternal"}>{intl.formatMessage({ id: "inheritance_paternal" })}</MenuItem>
                          <MenuItem value={"both"}>{intl.formatMessage({ id: "inheritance_both" })}</MenuItem>
                        </Select>
                      </Grid>
                    </>
                  )}
                </Grid>
                <div className={classes.iconsContainer}>
                  <IconButton onClick={() => handleOpenDiseaseModal(disease)}>
                    <VisibilityOutlinedIcon color={"secondary"} />
                  </IconButton>
                  <IconButton onClick={() => handleRemoveDisease(disease)}>
                    <HighlightOffRoundedIcon />
                  </IconButton>
                </div>
              </div>
            ))}
      </div>
      {!hideInheritance && !!selectedFamilyBackgrounds?.length && (
        <Typography variant={"caption"}>{intl.formatMessage({ id: "required_fields" })}</Typography>
      )}
      <DiseaseDetailDialog
        title={title}
        diseaseId={diseaseId}
        handleCloseDiseaseModal={handleCloseDiseaseModal}
        symptoms_score={symptoms_score}
      />
    </>
  );
};

export default SelectedBackgroundsComponent;
